import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useRequireAuth } from '../lib/auth';
import {
  useIsAppAdmin,
  useMyApprovalStatus,
  useMyWorkspaces,
  usePendingApprovals,
  type MyWorkspace,
} from '../lib/queries';
import { ORG_KINDS } from '../data/orgKinds';

const WEB3FORMS_KEY = '60a3c117-9305-42a2-90b1-4b5220315d42';

export function DashboardPage() {
  const { user, loading } = useRequireAuth();
  const approvalQ = useMyApprovalStatus(user?.id);
  const isAdminQ = useIsAppAdmin(user?.id);
  const isAdmin = !!isAdminQ.data;
  const pendingQ = usePendingApprovals(isAdmin);
  const approved = approvalQ.data === true;
  const workspacesQ = useMyWorkspaces(approved ? user?.id : undefined);

  if (loading || approvalQ.isLoading) {
    return <CenterText>Loading…</CenterText>;
  }
  if (!user) return null;

  // -----------------------------------------------------------
  // Unapproved branch — show pending state, fire notification once.
  // -----------------------------------------------------------
  if (!approved) {
    return (
      <PendingPanel
        userId={user.id}
        userEmail={user.email ?? ''}
        userName={(user.user_metadata?.full_name as string | undefined) ?? null}
      />
    );
  }

  // -----------------------------------------------------------
  // Approved branch — normal dashboard.
  // -----------------------------------------------------------
  const workspaces = workspacesQ.data ?? [];
  const hasWorkspaces = workspaces.length > 0;
  const pendingCount = pendingQ.data?.length ?? 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      {isAdmin && pendingCount > 0 && (
        <div className="border-l-2 border-accent pl-5 py-3 mb-8 flex items-baseline justify-between gap-4 flex-wrap bg-paper-deep">
          <div>
            <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent mb-1">
              Admin
            </div>
            <div className="font-editorial text-[15px] text-ink">
              {pendingCount} {pendingCount === 1 ? 'person is' : 'people are'} waiting for approval.
            </div>
          </div>
          <Link to="/admin/approvals" className="btn-text">
            Review →
          </Link>
        </div>
      )}
      {isAdmin && pendingCount === 0 && (
        <div className="mb-8 font-label text-[10px] uppercase tracking-[0.18em] text-ink-muted">
          Admin · <Link to="/admin/approvals" className="hover:text-accent">approval queue</Link>
        </div>
      )}

      <div className="eyebrow mb-4">Welcome back</div>
      <h1 className="editorial-h1 mb-3">
        Your <em className="italic text-accent">workspaces.</em>
      </h1>

      {workspacesQ.isLoading ? (
        <p className="font-editorial italic text-ink-muted mt-8">Loading your workspaces…</p>
      ) : workspacesQ.error ? (
        <p className="font-editorial italic text-[#a02525] mt-8">
          Couldn't load your workspaces — {(workspacesQ.error as Error).message}
        </p>
      ) : hasWorkspaces ? (
        <>
          <p className="editorial-deck mb-10 max-w-2xl">
            Pick a workspace to continue, or create another one — many LEAs run
            separate checklists for different fiscal years or initiatives.
          </p>

          <ul className="space-y-px bg-rule border border-rule mb-10">
            {workspaces.map((w) => (
              <li key={w.id}>
                <WorkspaceRow workspace={w} />
              </li>
            ))}
          </ul>

          <Link to="/workspace/new" className="btn-text">
            Create another workspace
          </Link>
        </>
      ) : (
        <>
          <p className="editorial-deck mb-10 max-w-2xl">
            Get started by creating a workspace for your LEA. You'll be set as
            the owner. Once it's live, you can invite teammates and start your
            CYBHI checklist.
          </p>

          <div className="border border-rule bg-paper-deep p-8 md:p-10 max-w-2xl">
            <div className="font-label text-[11px] uppercase tracking-[0.22em] text-accent mb-3">
              First-time setup
            </div>
            <h2 className="font-editorial text-[28px] font-medium text-ink mb-2 leading-tight">
              Create your first LEA workspace.
            </h2>
            <p className="font-editorial italic text-[16px] text-ink-soft mb-7 max-w-lg">
              Takes 15 seconds. You'll need a name (e.g. "Alameda COE — CYBHI FY26"
              or just "Our LEA") and a type — that's it.
            </p>
            <Link to="/workspace/new" className="btn-text">
              Create a workspace
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Shown to a signed-in user whose profile.approved_at is null.
 * Fires a one-shot Web3Forms POST so the founder gets an email the
 * first time this user lands here. Idempotent via localStorage so a
 * refresh doesn't re-notify.
 */
function PendingPanel({
  userId,
  userEmail,
  userName,
}: {
  userId: string;
  userEmail: string;
  userName: string | null;
}) {
  const { signOut } = useAuth();
  const notified = useRef(false);

  useEffect(() => {
    if (notified.current) return;
    const key = `cybhi.notify.${userId}`;
    if (localStorage.getItem(key)) return; // already sent
    notified.current = true;

    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: `CYBHI access request: ${userName ?? userEmail}`,
        name: userName ?? '(no name)',
        email: userEmail,
        message:
          'A new user signed up for the CYBHI Onboarding Checklist and confirmed their email. ' +
          'They are waiting for your approval before they can create or join workspaces. ' +
          `Approve them at: https://app.prajnainnovates.com/admin/approvals`,
      }),
    })
      .then(() => {
        try {
          localStorage.setItem(key, new Date().toISOString());
        } catch {
          /* localStorage disabled — best-effort */
        }
      })
      .catch((e) => {
        // Don't surface to the user; this is best-effort and the admin
        // queue is still authoritative via the DB.
        console.warn('[approval] notification failed:', e);
      });
  }, [userId, userEmail, userName]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
      <div className="eyebrow mb-4">Almost there</div>
      <h1 className="editorial-h1 mb-4">
        Your request is <em className="italic text-accent">on the way.</em>
      </h1>
      <p className="editorial-deck mb-10">
        Thanks for confirming your email. Access to the CYBHI Onboarding
        Checklist is approved by hand — Ash will review your request and you'll
        be able to sign in and create workspaces once it's approved.
      </p>

      <div className="border border-rule bg-paper-deep p-6 mb-10">
        <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent mb-2">
          In the meantime
        </div>
        <p className="font-editorial text-[15px] text-ink-soft leading-relaxed mb-4 max-w-lg">
          You can still take the{' '}
          <strong className="font-medium text-ink not-italic">
            Psychological Safety Assessment
          </strong>{' '}
          — it's open to everyone, no approval needed. Your results save to your
          account.
        </p>
        <Link to="/assessment" className="btn-text">
          Take the assessment →
        </Link>
      </div>

      <div className="border-t border-rule pt-6 font-editorial italic text-[14px] text-ink-muted flex items-baseline justify-between gap-4 flex-wrap">
        <span>
          Signed in as <strong className="font-medium not-italic text-ink">{userEmail}</strong>
        </span>
        <button
          type="button"
          onClick={() => signOut()}
          className="font-label text-[11px] uppercase tracking-[0.15em] text-link underline underline-offset-2 hover:text-accent not-italic"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function WorkspaceRow({ workspace }: { workspace: MyWorkspace }) {
  const kindLabel =
    ORG_KINDS.find((k) => k.id === workspace.kind)?.label ?? workspace.kind ?? '';
  return (
    <Link
      to={`/workspace/${workspace.id}`}
      className="block bg-paper p-5 hover:bg-paper-deep transition-colors group"
    >
      <div className="flex justify-between items-baseline gap-4 flex-wrap">
        <div>
          <div className="font-editorial text-[20px] font-medium text-ink leading-snug group-hover:text-accent transition-colors">
            {workspace.name}
          </div>
          <div className="font-editorial italic text-[14px] text-ink-muted mt-1">
            {kindLabel}
          </div>
        </div>
        <div className="font-label text-[10px] uppercase tracking-[0.18em] text-accent">
          {workspace.role}
        </div>
      </div>
    </Link>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
